import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { Favorite, FilterList, Refresh, Sort, Download } from '@mui/icons-material';
import { fetchPackages } from '../../store/slices/packageSlice';
import { PackageCard, SkeletonCard } from '../../components/ui';
import PaginationComponent from '../../components/ui/Pagination/PaginationComponent';
import { HeaderV2, FooterV2 } from '../../components/layout';
import { v2StickyBelowHeaderTop } from '../../components/layout/layoutConstants';
import { ScrollReveal } from '../../components/animations';
import { QuickViewModal } from '../../components/ui/Modals';
import { exportPackagesToExcel } from '../../utils/exportUtils';
import { DEMO_PACKAGES } from '../../data/demoPackages';

const PRICE_MIN = 0;
const PRICE_MAX = 10000;

const durationOptions = [
  { value: 'all', label: 'All Durations' },
  { value: 'short', label: 'Short (<=7 days)' },
  { value: 'medium', label: 'Medium (8-14 days)' },
  { value: 'long', label: 'Long (15+ days)' },
];

const categoryOptions = [
  { value: 'all', label: 'All trips' },
  { value: 'Umrah', label: 'Umrah' },
  { value: 'Ziyarat', label: 'Ziyarat' },
  { value: 'International', label: 'International' },
  { value: 'Pakistan', label: 'Pakistan' },
  { value: 'Local', label: 'Local' },
];

const ALL_TRIPS_SECTIONS = [
  { key: 'Umrah', title: 'Umrah packages', subtitle: 'Makkah & Madina — premium and economy' },
  { key: 'Ziyarat', title: 'Ziyarat & sacred journeys', subtitle: 'Iraq, Syria, Iran and holy sites' },
  { key: 'International', title: 'International trips', subtitle: 'Holidays abroad — prices in USD' },
  { key: 'Pakistan', title: 'Pakistan tours & getaways', subtitle: 'Northern areas and domestic — prices in PKR' },
  { key: 'Local', title: 'Local & weekend trips', subtitle: 'Short breaks near home — prices in PKR' },
];

const sortOptions = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'duration-asc', label: 'Duration: Short to Long' },
  { value: 'duration-desc', label: 'Duration: Long to Short' },
];

export const inferCategory = (pkg) => {
  if (pkg.productLine === 'Domestic' || pkg.productLine === 'Local') {
    return 'Pakistan';
  }
  if (pkg.productLine === 'Umrah') {
    return 'Umrah';
  }
  if (pkg.productLine === 'International') {
    return 'International';
  }
  if (pkg.category && ['Umrah', 'Ziyarat', 'International', 'Pakistan', 'Local'].includes(pkg.category)) {
    return pkg.category;
  }
  const name = (pkg.name || pkg.title || '').toLowerCase();
  const desc = (pkg.description || '').toLowerCase();
  const nightsMakkah = Number(pkg.nightsInMakkah || 0);
  const nightsMadina = Number(pkg.nightsInMadina || 0);

  // Pakistan domestic / local (before Umrah keyword heuristics)
  if (
    pkg.currency === 'PKR' ||
    pkg.tripScope === 'Domestic' ||
    pkg.tripScope === 'Local'
  ) {
    if (!name.includes('umrah') && !desc.includes('umrah')) {
      return 'Pakistan';
    }
  }

  // Umrah packages
  if (
    nightsMakkah > 0 ||
    nightsMadina > 0 ||
    name.includes('umrah') ||
    desc.includes('umrah') ||
    name.includes('ramadan') ||
    ((name.includes('makkah') || desc.includes('makkah')) && !name.includes('hunza') && !name.includes('skardu'))
  ) {
    return 'Umrah';
  }

  // Ziyarat packages
  if (
    name.includes('ziyarat') ||
    name.includes('ziaraat') ||
    desc.includes('ziyarat') ||
    desc.includes('karbala') ||
    desc.includes('najaf') ||
    name.includes('karbala') ||
    name.includes('iraq') ||
    name.includes('syria') ||
    name.includes('iran') ||
    (Array.isArray(pkg.ziyarats) && pkg.ziyarats.length > 0)
  ) {
    return 'Ziyarat';
  }

  // Pakistan domestic packages
  if (
    pkg.currency === 'PKR' ||
    pkg.tripScope === 'Domestic' ||
    pkg.tripScope === 'Local' ||
    name.includes('hunza') ||
    name.includes('skardu') ||
    name.includes('swat') ||
    name.includes('kalam') ||
    name.includes('naran') ||
    name.includes('kaghan') ||
    name.includes('shogran') ||
    name.includes('murree') ||
    name.includes('bhurban') ||
    name.includes('lahore') ||
    name.includes('pakistan')
  ) {
    return 'Pakistan';
  }

  // Holiday / International trips
  return 'International';
};

const PackagesPageV2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { packages = [], loading } = useSelector((state) => state.packages);

  const [searchTerm, setSearchTerm] = useState(location.state?.search || '');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState('recommended');
  const [quickViewPkg, setQuickViewPkg] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    dispatch(fetchPackages());
  }, [dispatch]);

  // Synchronize category and search from URL or navigation state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const catParam = params.get('category');

    if (catParam) {
      if (catParam.toLowerCase() === 'trips' || catParam.toLowerCase() === 'international') {
        setCategoryFilter('International');
      } else if (catParam.toLowerCase() === 'umrah') {
        setCategoryFilter('Umrah');
      } else if (catParam.toLowerCase() === 'ziyarat' || catParam.toLowerCase() === 'zyaraat') {
        setCategoryFilter('Ziyarat');
      } else if (catParam.toLowerCase() === 'pakistan') {
        setCategoryFilter('Pakistan');
      } else {
        setCategoryFilter(catParam);
      }
      setSearchTerm('');
    } else if (location.state?.search) {
      const q = location.state.search;
      if (q.toLowerCase() === 'umrah') {
        setCategoryFilter('Umrah');
        setSearchTerm('');
      } else if (q.toLowerCase() === 'ziyarat' || q.toLowerCase() === 'zyaraat') {
        setCategoryFilter('Ziyarat');
        setSearchTerm('');
      } else {
        setSearchTerm(q);
      }
    }
  }, [location.search, location.state]);

  const catalog = packages?.length ? packages : DEMO_PACKAGES;

  const filteredPackages = useMemo(() => {
    const normalize = (val) => (val || 0);

    return catalog
      .filter((pkg) => {
        const name = (pkg.name || pkg.title || '').toLowerCase();
        const desc = (pkg.description || '').toLowerCase();
        const pkgCat = inferCategory(pkg);
        const q = searchTerm.toLowerCase();
        const searchMatches =
          !q ||
          name.includes(q) ||
          desc.includes(q) ||
          pkgCat.toLowerCase().includes(q) ||
          (pkg.destinationLabel || '').toLowerCase().includes(q);

        const categoryMatches =
          categoryFilter === 'all' ||
          pkgCat.toLowerCase() === categoryFilter.toLowerCase() ||
          (categoryFilter === 'Trips' && (pkgCat === 'International' || pkgCat === 'Pakistan'));

        const duration = normalize(pkg.duration || pkg.nights);
        const durationMatches =
          durationFilter === 'all' ||
          (durationFilter === 'short' && duration <= 7) ||
          (durationFilter === 'medium' && duration >= 8 && duration <= 14) ||
          (durationFilter === 'long' && duration >= 15);

        const rawPrice = normalize(pkg.basePrice ?? pkg.price);
        const isPkr =
          pkg.currency === 'PKR' ||
          pkgCat === 'Pakistan' ||
          pkg.tripScope === 'Domestic' ||
          pkg.tripScope === 'Local';
        const normalizedUsdPrice = isPkr ? rawPrice / 280 : rawPrice;
        const priceMatches = normalizedUsdPrice >= priceRange[0] && normalizedUsdPrice <= priceRange[1];
        const ratingMatches = normalize(pkg.rating) >= minRating;

        return searchMatches && categoryMatches && durationMatches && priceMatches && ratingMatches;
      })
      .sort((a, b) => {
        const pa = a.basePrice ?? a.price ?? 0;
        const pb = b.basePrice ?? b.price ?? 0;
        const da = a.duration || a.nights || 0;
        const db = b.duration || b.nights || 0;
        if (sort === 'price-asc') return pa - pb;
        if (sort === 'price-desc') return pb - pa;
        if (sort === 'duration-asc') return da - db;
        if (sort === 'duration-desc') return db - da;
        return 0;
      });
  }, [catalog, searchTerm, categoryFilter, durationFilter, priceRange, minRating, sort]);

  const packagesBySection = useMemo(() => {
    const map = {};
    filteredPackages.forEach((pkg) => {
      const cat = inferCategory(pkg);
      if (!map[cat]) map[cat] = [];
      map[cat].push(pkg);
    });
    return map;
  }, [filteredPackages]);

  const packageCardProps = (pkg, idx) => ({
    image: pkg.packageImages?.[0]?.imageUrl || pkg.image || pkg.packageImage || '/placeholder.jpg',
    title: pkg.name || pkg.title,
    description: pkg.description || pkg.summary || pkg.destinationLabel || 'Curated trip package',
    price: pkg.basePrice || pkg.price || 0,
    currency: pkg.currency,
    category: pkg.category,
    duration: pkg.duration || pkg.nights || 0,
    rating: pkg.rating || pkg.averageRating || 4.5,
    badges:
      pkg.badges?.length
        ? pkg.badges
        : (pkg.nightsInMakkah || pkg.nightsInMadina)
          ? [
              { label: `Makkah: ${pkg.nightsInMakkah || 0}N`, color: '#1F6F78' },
              { label: `Madina: ${pkg.nightsInMadina || 0}N`, color: '#2A8F9A' },
            ]
          : pkg.category
            ? [{ label: pkg.category, color: '#1F6F78' }]
            : [],
    onQuickView: () => setQuickViewPkg(pkg),
    onClick: () => navigate(`/v2/packages/${pkg.id || ''}`),
  });

  const renderPackageGrid = (list, keyPrefix = '') => (
    <Grid container spacing={3}>
      {list.map((pkg, idx) => (
        <Grid item xs={12} sm={6} lg={4} key={pkg.id || `${keyPrefix}-${idx}`}>
          <ScrollReveal animation="fadeInUp" delay={idx * 0.05}>
            <PackageCard {...packageCardProps(pkg, idx)} />
          </ScrollReveal>
        </Grid>
      ))}
    </Grid>
  );

  const handleReset = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setDurationFilter('all');
    setPriceRange([0, 5000]);
    setMinRating(0);
    setSort('recommended');
    setCurrentPage(1);
    navigate('/v2/packages', { replace: true });
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />

      <Box component="main" sx={{ py: 6, scrollMarginTop: v2StickyBelowHeaderTop }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Filters */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                  position: 'sticky',
                  top: v2StickyBelowHeaderTop,
                  zIndex: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FilterList fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Filters
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Favorite />}
                    onClick={() => navigate('/v2/dashboard')}
                  >
                    My Picks
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  label="Search packages"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 3 }}
                />

                <FormControl fullWidth sx={{ mb: 3 }} size="small">
                  <InputLabel>Trip type</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Trip type"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    {categoryOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }} size="small">
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={durationFilter}
                    label="Duration"
                    onChange={(e) => setDurationFilter(e.target.value)}
                  >
                    {durationOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Price Range
                  </Typography>
                  <Slider
                    value={priceRange}
                    onChange={(e, val) => setPriceRange(val)}
                    valueLabelDisplay="auto"
                    min={PRICE_MIN}
                    max={PRICE_MAX}
                    step={100}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ${priceRange[0]} - ${priceRange[1]}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Minimum Rating
                  </Typography>
                  <Slider
                    value={minRating}
                    onChange={(e, val) => setMinRating(val)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={5}
                    step={0.5}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {minRating} and up
                  </Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }} size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sort}
                    label="Sort By"
                    onChange={(e) => setSort(e.target.value)}
                  >
                    {sortOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Sort fontSize="small" /> {opt.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(categoryFilter !== 'all' || durationFilter !== 'all' || minRating > 0 || searchTerm) && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {searchTerm && (
                      <Chip label={`Search: ${searchTerm}`} onDelete={() => setSearchTerm('')} size="small" />
                    )}
                    {categoryFilter !== 'all' && (
                      <Chip label={`Type: ${categoryFilter}`} onDelete={() => setCategoryFilter('all')} size="small" />
                    )}
                    {durationFilter !== 'all' && (
                      <Chip label={`Duration: ${durationFilter}`} onDelete={() => setDurationFilter('all')} size="small" />
                    )}
                    {minRating > 0 && (
                      <Chip label={`Rating: ${minRating}+`} onDelete={() => setMinRating(0)} size="small" />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Results */}
            <Grid item xs={12} md={9}>
              {/* Category Quick Tabs */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {[
                  { label: 'All Trips', value: 'all' },
                  { label: 'Umrah', value: 'Umrah' },
                  { label: 'Ziyarat', value: 'Ziyarat' },
                  { label: 'International Trips', value: 'International' },
                  { label: 'Pakistan Tours', value: 'Pakistan' },
                ].map((cat) => (
                  <Chip
                    key={cat.value}
                    label={cat.label}
                    clickable
                    color={categoryFilter.toLowerCase() === cat.value.toLowerCase() ? 'primary' : 'default'}
                    variant={categoryFilter.toLowerCase() === cat.value.toLowerCase() ? 'filled' : 'outlined'}
                    onClick={() => {
                      setCategoryFilter(cat.value);
                      navigate(cat.value === 'all' ? '/v2/packages' : `/v2/packages?category=${cat.value}`, { replace: true });
                    }}
                    sx={{
                      fontWeight: categoryFilter.toLowerCase() === cat.value.toLowerCase() ? 700 : 500,
                      px: 0.5,
                      py: 0.5,
                      fontSize: '0.88rem',
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {categoryFilter === 'Umrah'
                      ? 'Umrah Packages'
                      : categoryFilter === 'Ziyarat'
                      ? 'Ziyarat & Sacred Journeys'
                      : categoryFilter === 'International'
                      ? 'Holiday & International Trips'
                      : categoryFilter === 'Pakistan'
                      ? 'Pakistan Tours & Getaways'
                      : 'All Trips & Packages'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {categoryFilter === 'Umrah'
                      ? `${filteredPackages.length} packages · Makkah & Madina premium & economy packages`
                      : categoryFilter === 'Ziyarat'
                      ? `${filteredPackages.length} packages · Iraq, Karbala, Syria & holy sites`
                      : categoryFilter === 'International'
                      ? `${filteredPackages.length} packages · Dubai, UK, Europe, Far East & holidays`
                      : categoryFilter === 'Pakistan'
                      ? `${filteredPackages.length} packages · Northern valleys, Hunza, Swat & heritage tours`
                      : `${filteredPackages.length} results · Umrah, ziyarat, holidays & domestic tours`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/v2/customized-booking')}
                    size="small"
                  >
                    Customize My Trip
                  </Button>
                  {filteredPackages.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => exportPackagesToExcel(filteredPackages, 'Rehman_Travel_Trips.xlsx')}
                      size="small"
                    >
                      Export to Excel
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {loading && packages.length === 0 ? (
                <Grid container spacing={3}>
                  {[...Array(6)].map((_, idx) => (
                    <Grid item xs={12} sm={6} lg={4} key={idx}>
                      <SkeletonCard />
                    </Grid>
                  ))}
                </Grid>
              ) : filteredPackages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, borderRadius: 3, backgroundColor: 'white', boxShadow: '0 10px 30px rgba(0,0,0,0.06)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    No packages match your filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Try adjusting your search or filters to find more options.
                  </Typography>
                  <Button variant="contained" onClick={handleReset}>
                    Reset Filters
                  </Button>
                </Box>
              ) : categoryFilter === 'all' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {ALL_TRIPS_SECTIONS.map((section) => {
                    const list = packagesBySection[section.key] || [];
                    if (!list.length) return null;
                    return (
                      <Box key={section.key}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {section.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {list.length} package{list.length === 1 ? '' : 's'} · {section.subtitle}
                        </Typography>
                        {renderPackageGrid(list, section.key)}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <>
                  {renderPackageGrid(
                    filteredPackages.slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage
                    ),
                    'page'
                  )}

                  <PaginationComponent
                    currentPage={currentPage}
                    totalItems={filteredPackages.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                    showPageInfo
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      <FooterV2 />

      {quickViewPkg && (
        <QuickViewModal
          open={Boolean(quickViewPkg)}
          onClose={() => setQuickViewPkg(null)}
          package={quickViewPkg}
        />
      )}
    </Box>
  );
};

export default PackagesPageV2;
